import React from 'react'
import { FormControl } from 'react-bootstrap'
import { useUser } from '@ztechapps/common.hook'
import type { DateType } from '@ztechapps/common.utils'
import { getBrowserTimeZone, isUtcIsoString, toDate } from '@ztechapps/common.utils'
import type { FieldEditorInjectedProps } from '@ztechapps/webui.input'
import isEmpty from 'lodash/isEmpty'

export type DateTimeInputProps = {
  type?: 'datetime-local' | 'date' | 'time' | 'month'
  value?: DateType
  onChange?: (value: DateType) => void
  properValue?: any
  timeZone?: string
  min?: string
  max?: string
  disabled?: boolean
  submitting?: boolean
  style?: Record<any, any>
  inputRef?: React.RefObject<HTMLInputElement> | any
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  id?: string
} & FieldEditorInjectedProps
/**
 *  This the native HTML DateTime input, it works fine on Chrome, Edge, but safari and firefox, NO
 */
export const DateTimeInput = ({
  type = 'datetime-local',
  value,
  onChange,
  properValue,
  timeZone,
  disabled,
  submitting,
  inputRef,
  form: _form, // `FieldEditor` injects Formik's `form`/`meta`/`isDisabled` into every `<Field component={X}>`; discarded here so they never reach the DOM `<input>` via `...rest`
  meta: _meta,
  isDisabled: _isDisabled,
  ...rest
}: DateTimeInputProps) => {
  const { user } = useUser()
  // Timezone fallback chain (most-specific to least-specific):
  //   1. Explicit timeZone prop (caller override)
  //   2. Authenticated user's configured timezone (user profile preference)
  //   3. Browser's IANA timezone via Intl API (matches what the user's OS reports)
  //   4. 'America/Chicago' as a last-resort hard fallback
  //
  // The browser timezone is included as a safer fallback than the hardcoded
  // 'America/Chicago' so unauthenticated users or users without a profile
  // timeZone still get correct local time based on their browser.
  timeZone = timeZone ?? user?.timeZone ?? getBrowserTimeZone() ?? 'America/Chicago'

  properValue = React.useMemo(() => {
    if (properValue) return properValue({ value })

    let format = 'YYYY-MM-DDTHH:mm'

    if (type === 'date') {
      format = 'YYYY-MM-DD'
    } else if (type === 'time') {
      format = 'HH:mm'
    } else if (type === 'month') {
      format = 'YYYY-MM'
    }

    // For UTC ISO values (Z-suffixed, e.g. "2026-04-04T15:59:00.000Z") the incoming
    // value already carries an unambiguous UTC instant. Convert it to the user's
    // timezone WITHOUT keepLocalTime so the wall clock reflects the user's local
    // time (e.g. 23:59 Manila from 15:59 UTC).
    //
    // For legacy/naive values (e.g. "2026-04-04T23:59") we use keepLocalTime:true
    // to preserve the historical behavior — parse the naive string as already being
    // in the user's local time.
    const toDateResult = isUtcIsoString(value)
      ? toDate(value, timeZone)
      : toDate(value, timeZone, { keepLocalTime: true })

    if (!toDateResult.isValid()) return '' //return as empty string if not valid date

    //the proper value / normalized value was always in the string format
    return toDateResult.format(format)
  }, [value, properValue, timeZone, type])

  const finalOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    let changedValue = e?.target?.value
    //if empty value just returns it
    if (isEmpty(changedValue)) return onChange(changedValue)

    if (type === 'time') {
      changedValue = `${toDate(undefined, timeZone, { keepLocalTime: true }).format('yyyy-MM-DD')} ${changedValue}`
    }

    // For datetime-local (and time, which is stored as a full datetime with today's date
    // prepended above), emit a UTC ISO string so downstream code (Mongoose Date field,
    // backend server) stores the correct instant regardless of where the server runs.
    //
    // Before: emitted naive string like "2026-04-04T23:59" which gets re-parsed with
    // the SERVER's local timezone, causing a long-standing bug when server region
    // differs from user region (e.g. NY server + Manila user = wrong stored UTC).
    //
    // The HTML <input type="datetime-local"> never sees this Z-suffixed string — the
    // properValue memo above converts it back to YYYY-MM-DDTHH:mm for display. The
    // change only affects the value that flows up to the parent via onChange.
    //
    // For date/month types we keep the current naive format because calendar dates
    // ("April 4" as a day, not an instant) should not shift across timezones.
    if (type === 'datetime-local' || type === 'time' || !type) {
      changedValue = toDate(changedValue, timeZone, { keepLocalTime: true }).toISOString()
    } else {
      //we hi-jack here to return as string, that is what the HTML date time input wants
      changedValue = toDate(changedValue, timeZone, { keepLocalTime: true }).format('YYYY-MM-DDTHH:mm')
    }

    return onChange(changedValue)
  }

  const onFocus = () => {
    inputRef?.current?.showPicker?.()
  }

  //the min and max should be handled accordingly

  return (
    <FormControl
      ref={inputRef}
      onFocus={onFocus}
      disabled={disabled || submitting}
      type={type}
      {...rest}
      value={properValue}
      onChange={finalOnChange}
    />
  )
}
